import numeral from 'numeral'
import { useEffect, useMemo } from 'react'

import { useSort } from 'src/providers/SortProvider'

import { Card } from 'src/components/atoms/Card'
import { LoadingSpinner } from 'src/components/atoms/LoadingSpinner'
import { Heading1, Paragraph } from 'src/components/atoms/Typography'
import { FilterControls } from 'src/components/molecules/FilterControls'
import { PaginationRebrand } from 'src/components/molecules/PaginationRebrand'
import { buildRegionFilterOptions } from 'src/components/molecules/RegionSelect/helpers'
import { SearchControls } from 'src/components/molecules/SearchControls'
import { AdminApplicationsTable } from 'src/components/organisms/tables/AdminApplicationsTable'
import { VStack } from 'src/components/templates/VStack'

import {
  useListAdminApplicationsViewLazyQuery,
  useListRegionsForSearchApplicationsQuery,
  useListSeasonsQuery
} from 'src/config/generated/graphql'
import { useFilters } from 'src/hooks/useFilters'
import { usePagination } from 'src/hooks/usePagination'
import { useSearch } from 'src/hooks/useSearch'
import {
  AdminListApplicationViewType,
  ApplicationWorkflowState
} from 'src/types/application'
import {
  buildListApplicationsOrderBy,
  buildListApplicationsWhere
} from 'src/utils/application'

export const AdminListApplications = () => {
  const [
    listApplications,
    { data: applicationsData, loading: applicationsLoading }
  ] = useListAdminApplicationsViewLazyQuery({
    fetchPolicy: 'cache-and-network'
  })

  const { data: regionsData } = useListRegionsForSearchApplicationsQuery()

  const { data: seasonsData } = useListSeasonsQuery()

  const totalCount = useMemo(() => {
    return applicationsData?.ApplicationView_aggregate?.aggregate?.count || 0
  }, [applicationsData?.ApplicationView_aggregate])

  const { currentPage, limit, offset, onPageClick } = usePagination()

  const { filters, setFilters, clearFilters } = useFilters({
    supportedFilters: [
      'workflowStates',
      'isMatched',
      'season',
      'region',
      'onHoldStatus'
    ]
  })

  const { searchQuery, setSearchQuery, clearSearchQuery } = useSearch()

  const { sort } = useSort()

  useEffect(() => {
    const where = buildListApplicationsWhere({
      filters,
      searchQuery: searchQuery as string
    })

    const orderBy = buildListApplicationsOrderBy(sort)

    listApplications({ variables: { offset, where, order_by: orderBy } })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, limit, offset, searchQuery])

  const applicationFilters = useMemo(() => {
    const regionsFilter = buildRegionFilterOptions(regionsData?.Region)

    const seasonFilter =
      seasonsData?.Season.map((s: any) => ({
        id: s.Name,
        name: s.Name
      })) || []

    return [
      {
        id: 'workflowStates',
        title: 'Status',
        hideAllOption: true,
        options: [
          {
            id: 'all',
            name: 'See all'
          },
          {
            id: '__ApplicationStateGroup',
            name: 'Planting/Application State'
          },
          {
            id: ApplicationWorkflowState.Draft,
            name: 'Draft'
          },
          {
            id: ApplicationWorkflowState.Pending,
            name: 'Pending (submitted)'
          },
          {
            id: ApplicationWorkflowState.Approved,
            name: 'Approved'
          },
          {
            id: ApplicationWorkflowState.Planted,
            name: 'Planted'
          },
          {
            id: ApplicationWorkflowState.Monitoring,
            name: 'Monitoring'
          },
          {
            id: '__FundingStatusGroup',
            name: 'Funding Status'
          },
          {
            id: `${ApplicationWorkflowState.Approved},${ApplicationWorkflowState.Planted},${ApplicationWorkflowState.Monitoring}`,
            name: 'Funding Approved'
          },
          {
            id: `${ApplicationWorkflowState.Declined}`,
            name: 'Funding Declined'
          },
          {
            id: `${ApplicationWorkflowState.Withdrawn}`,
            name: 'Funding Withdrawn'
          }
        ]
      },
      {
        id: 'isMatched',
        title: 'Fully matched',
        options: [
          { id: 'true', name: 'Yes' },
          { id: 'false', name: 'No' }
        ]
      },
      {
        id: 'season',
        title: 'Season',
        options: seasonFilter
      },
      {
        id: 'region',
        title: 'Region',
        options: regionsFilter
      },
      {
        id: 'onHoldStatus',
        title: 'On Hold',
        options: [
          { id: 'true', name: 'On hold' },
          { id: 'false', name: 'Not on hold' }
        ]
      }
    ]
  }, [regionsData, seasonsData])

  return (
    <VStack>
      <Heading1>Tree requests</Heading1>
      <LoadingSpinner isLoading={applicationsLoading}>
        <Paragraph>Total: {numeral(totalCount).format('0,0')}</Paragraph>
        <Card className='my-4 overflow-x-scroll' size='small'>
          <div>
            <FilterControls
              filters={applicationFilters}
              onChange={setFilters}
              onClear={clearFilters}
              selectedFilters={filters}
              showOnClear
            />
          </div>
          <div className='md:flex justify-between md:space-x-2'>
            <SearchControls
              // TODO: next/router allows both a string and an array of strings as a query param,
              // but search controls doesn't support that, so we should probably handle this at some point...
              // In the meantime, we cast (:
              currentSearchQuery={searchQuery as string}
              onSearch={setSearchQuery}
              onClear={clearSearchQuery}
            />
          </div>
          <AdminApplicationsTable
            applicationsData={
              (applicationsData?.ApplicationView ??
                []) as AdminListApplicationViewType[]
            }
          />
          <PaginationRebrand
            count={totalCount}
            onChange={(page: number) => onPageClick(page)}
            page={currentPage}
            pageSize={limit}
            showQuickJumper
          />
        </Card>
      </LoadingSpinner>
    </VStack>
  )
}
